/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../logger';
import { Engine } from '../rules/engine';
import { AdIdType, CampaignType } from '../types/index';
import { ViewManager } from './activity';
import { Ad } from './ad';

import type { Facts } from '../rules';
import type { AdAssignedKeyValues, AdData, CampaignData, KeyValue } from '../types/index';
import type { HierarchyNode } from './activity';

const logger = Logger.getInstance();

class Campaign {
  private ads: Ad[] = [];
  private data: CampaignData;

  constructor(campaignData: CampaignData, facts: Facts) {
    this.data = { ...campaignData };
    this.ads = this.data.ads.map((adData) => new Ad(this.mergeRules(adData), facts));
    this.setViewsFact();
  }

  public get adDeliveryIds(): string[] {
    return this.ads.map((ad) => ad.deliveryId);
  }

  public get id(): string {
    return this.data.id;
  }

  public get type(): CampaignType {
    return this.data.type;
  }

  public get deals(): string[] {
    return this.data?.deals || [];
  }

  public get linkedExternalLineIds(): string[] {
    const linkedLineIds = Array.from(
      new Set(
        this.ads
          .map((ad) => ad.linkedLineId)
          .filter((id): id is string => id !== null && id !== undefined)
      )
    );
    return linkedLineIds;
  }

  public getMatchingAdDeliveryId(lineId: string): string | undefined {
    const matchingAd = this.ads.find((ad) => ad.linkedLineId === lineId);
    if (matchingAd) {
      return matchingAd.deliveryId;
    }
  }

  public getEligibleAdIds(): string[] {
    const filteredAds = this.ads.filter((ad) => ad.isEligible());
    return filteredAds.map((ad) => ad.deliveryId);
  }

  public get keyValuesByAd(): AdAssignedKeyValues[] {
    return this.ads.map((ad) => ({
      id: ad.deliveryId,
      kvs: ad.keyValues,
    }));
  }

  public get keyValues(): KeyValue[] {
    const kvs: KeyValue[] = [];
    this.ads.forEach((ad) => {
      logger.debug(`Retrieved key-values for ad ${ad.deliveryId}: ${JSON.stringify(ad.keyValues)}`);
      ad.keyValues.forEach((kv) => {
        const existingKv = kvs.find((existingKv) => existingKv.key === kv.key);
        if (!existingKv) {
          kvs.push(kv);
        } else {
          const existingValues = existingKv.value.split(',');
          if (!existingValues.includes(kv.value)) {
            existingKv.value += `,${kv.value}`;
          }
        }
      });
    });
    return kvs;
  }

  public set adKeyValues(adAssignedKvs: AdAssignedKeyValues[]) {
    if ([CampaignType.LEARNING_DIRECT, CampaignType.LEARNING_PROGRAMMATIC].includes(this.type)) {
      // Never set key values for learning campaigns
      return;
    }
    adAssignedKvs.forEach((adAssignedKv) => {
      const ad = this.ads.find((a) => a.deliveryId === adAssignedKv.id);
      if (ad) {
        ad.keyValues = adAssignedKv.kvs ?? [];
      }
    });
  }

  public isAssociatedWith(externalId: string): boolean {
    logger.debug(`Checking campaign association with ad server line external ID: ${externalId}`);
    return this.ads.some((ad) => ad.linkedLineId === externalId);
  }

  public getAdKeyValues(id: string, type: AdIdType): KeyValue[] {
    logger.info(`Fetching key values for reporting ad with ID: ${id} of type: ${type}`);
    const matchingAd = this.ads.find((ad) =>
      type === AdIdType.EXTERNAL_ID ? ad.linkedLineId === id : ad.deliveryId === id
    );
    return matchingAd ? matchingAd.keyValues : [];
  }

  private setViewsFact(): void {
    const hierarchy: HierarchyNode = {
      id: this.id,
      children: this.ads.map((ad) => ({ id: ad.deliveryId, children: [] })),
    };
    const campaignViews = ViewManager.getViewsForEntity(this.id, hierarchy);
    this.ads.forEach((ad) => {
      ad.addFacts({ 'campaign.views': campaignViews });
    });
  }

  private mergeRules(adData: AdData): AdData {
    if (!this.data.rules) {
      return adData;
    }

    const mergedAdData = { ...adData };
    const baseConditions = mergedAdData?.rules?.conditions || {};
    const additionalConditions = this.data?.rules?.conditions || {};

    mergedAdData.rules = {
      conditions: Engine.mergeConditions(baseConditions, additionalConditions),
    };

    return mergedAdData;
  }
}

export { Campaign };
